'use client'

import { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { useSupabase } from '@/hooks/use-supabase';
import { useAuth } from '@/components/auth/auth-provider';
import { formatCurrency } from '@/lib/utils';
import { Camera, X, Loader2, Upload, MousePointerSquareDashed, CameraIcon, Save, Video } from 'lucide-react';
import { useDropzone } from 'react-dropzone';
import imageCompression from 'browser-image-compression';
import { SupabaseClient } from '@supabase/supabase-js';

interface ScannedTicket {
  amount: number;
  description: string;
  date: string;
  merchant?: string;
  category?: string;
  imageFile?: File;
  originalPdfUrl?: string | null;
}

interface Category {
  id: string;
  name: string;
  icon: string;
  color: string;
}

interface ScannerState {
  isScanning: boolean;
  scannedTickets: ScannedTicket[];
  selectedFiles: File[];
  error: string;
  categories: Category[];
  loadingCategories: boolean;
  isMobile: boolean;
  statusMessage: string;
}

interface TicketScannerProps {
  onTicketProcessed?: (tickets: ScannedTicket | ScannedTicket[]) => void;
  onClose?: () => void;
}

const TicketScanner = ({ onTicketProcessed, onClose }: TicketScannerProps): JSX.Element => {
  const { user } = useAuth();
  const supabase = useSupabase();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const pickerInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  const [state, setState] = useState<ScannerState>({
    isScanning: false,
    scannedTickets: [],
    selectedFiles: [],
    error: '',
    categories: [],
    loadingCategories: true,
    isMobile: false,
    statusMessage: ''
  });

  // Typed state updater
  const setStateValue = useCallback((updates: Partial<ScannerState> | ((prev: ScannerState) => Partial<ScannerState>)) => {
    if (typeof updates === 'function') {
      setState(prev => ({
        ...prev,
        ...updates(prev)
      }));
    } else {
      setState(prev => ({
        ...prev,
        ...updates
      }));
    }
  }, []);

  // Función para obtener el ID de categoría por nombre o sugerencia
  const getCategoryId = useCallback((categoryName: string): string => {
    if (!categoryName || !state.categories.length) return state.categories[0]?.id || '';

    const normalizedInput = categoryName.toLowerCase().trim();

    // Primero intentar hacer match exacto
    const exactMatch = state.categories.find(cat =>
      cat.name.toLowerCase() === normalizedInput
    );

    if (exactMatch) return exactMatch.id;

    // Mapeo de categorías comunes con coincidencias parciales
    const categoryMap: { [key: string]: string } = {
      'seguro': 'Seguros',
      'seguros': 'Seguros',
      'salud': 'Salud',
      'alimentacion': 'Alimentación',
      'comida': 'Alimentación',
      'restaurante': 'Alimentación',
      'supermercado': 'Alimentación',
      'transporte': 'Transporte',
      'gasolina': 'Transporte',
      'estacionamiento': 'Transporte',
      'educacion': 'Educación',
      'escuela': 'Educación',
      'universidad': 'Educación',
      'cursos': 'Educación',
      'entretenimiento': 'Entretenimiento',
      'ocio': 'Entretenimiento',
      'cine': 'Entretenimiento',
      'streaming': 'Entretenimiento',
      'vivienda': 'Vivienda',
      'alquiler': 'Vivienda',
      'hipoteca': 'Vivienda',
      'servicio': 'Servicios',
      'servicios': 'Servicios',
      'luz': 'Servicios',
      'agua': 'Servicios',
      'internet': 'Servicios',
      'telefono': 'Servicios',
      'general': 'General'
    };



    // Buscar coincidencia exacta en el mapa
    const mappedCategory = categoryMap[normalizedInput];


    if (mappedCategory) {
      const found = state.categories.find(cat =>
        cat.name.toLowerCase() === mappedCategory.toLowerCase()
      );

      if (found) return found.id;
    }

    // Buscar coincidencia parcial en los nombres de categoría
    const partialMatch = state.categories.find(cat =>
      cat.name.toLowerCase().includes(normalizedInput) ||
      normalizedInput.includes(cat.name.toLowerCase())
    );

    if (partialMatch) return partialMatch.id;

    // Si no se encuentra ninguna coincidencia, devolver la primera categoría o vacío

    return state.categories[0]?.id || '';
  }, [state.categories]);

  // Convertir FileList a array de Files
  const fileListToArray = (fileList: FileList | File[] | null): File[] => {
    if (!fileList) return [];
    return Array.isArray(fileList) ? fileList : Array.from(fileList);
  }

  // Función única para comprimir imágenes
  const compressImage = useCallback(async (file: File): Promise<File> => {
    try {
      // Configuración optimizada para móviles
      const options = {
        maxSizeMB: 0.5,            // Tamaño máximo del archivo (0.5MB)
        maxWidthOrHeight: 1200,     // Máxima dimensión
        useWebWorker: true,         // Usar web worker para no bloquear la UI
        maxIteration: 10,           // Máximo número de iteraciones de compresión
        fileType: file.type || 'image/jpeg', // Mantener tipo original
        initialQuality: 0.8,        // Calidad inicial (0-1)
        alwaysKeepResolution: false, // Permitir redimensionar para mantener el tamaño
      };



      const compressedFile = await imageCompression(file, options);


      return new File([compressedFile], file.name, { type: file.type });
    } catch (error) {
      console.error('Error en compresión:', error);
      return file; // Return original if compression fails
    }
  }, []);

  // Definir el tipo para el evento personalizado
  type CustomFileInputEvent = {
    target: {
      files: File[] | FileList | null;
    };
  };

  // (PDF deshabilitado) — Solo imágenes

  // Función para manejar selección de archivos
  const handleFileSelect = useCallback(async (event: React.ChangeEvent<HTMLInputElement> | CustomFileInputEvent) => {
    const files = fileListToArray('target' in event ? event.target.files : null);
    // Solo imágenes

    if (!files.length) return;

    // Validar cantidad de archivos
    if (files.length > 5) {
      setStateValue({
        error: 'Máximo 5 archivos a la vez',
        statusMessage: ''
      });
      return;
    }

    // Validar tipos: solo imágenes
    const isSupported = (file: File) => (file.type || '').toLowerCase().startsWith('image/')

    const invalidFiles = files.filter(file => !isSupported(file))
    if (invalidFiles.length > 0) {
      // Archivos no soportados (PDF u otros)
    }
    if (invalidFiles.length > 0) {
      setStateValue({
        error: `Formato no soportado: ${invalidFiles.map(f => f.name).join(', ')}. Usa JPG, PNG o WebP.`,
        statusMessage: ''
      })
      return
    }

    // Preparar archivos: si es PDF, convertir primera página a imagen y marcar original
    try {
      const processedFiles: File[] = []
      for (const file of files) {
        // Imagen normal (posible compresión en móvil)
        const img = state.isMobile ? await compressImage(file) : file
        processedFiles.push(img)
      }

      if (processedFiles.length === 0) {
        setStateValue(prev => ({ ...prev, error: 'Ningún archivo válido para escanear. Verifica el formato o el tamaño.' }))
      } else {
        setStateValue(prev => ({
          ...prev,
          selectedFiles: [...prev.selectedFiles.filter(f => !files.includes(f)), ...processedFiles],
          error: ''
        }))
      }
    } catch (error) {
      console.error('Error procesando imágenes:', error);
    }
  }, [state.isMobile, compressImage, setStateValue]);

  // Helpers estabilizados para categorías
  const ensureAllCategories = useCallback(async (existingCategories: any[]) => {
    if (!user) return;

    const requiredCategories = [
      { name: 'Alimentación', icon: 'ShoppingCart', color: '#10B981' },
      { name: 'Vivienda', icon: 'Home', color: '#3B82F6' },
      { name: 'Transporte', icon: 'Bus', color: '#F59E0B' },
      { name: 'Servicios', icon: 'Zap', color: '#8B5CF6' },
      { name: 'Salud', icon: 'HeartPulse', color: '#EF4444' },
      { name: 'Entretenimiento', icon: 'Film', color: '#EC4899' },
      { name: 'Deudas y Préstamos', icon: 'Landmark', color: '#7E22CE' },
      { name: 'Educación', icon: 'BookOpen', color: '#06B6D4' },
      { name: 'Comunicación', icon: 'Phone', color: '#059669' },
      { name: 'Seguridad Social', icon: 'Users', color: '#D97706' },
      { name: 'Seguros', icon: 'ShieldCheck', color: '#6366F1' },
      { name: 'General', icon: 'Folder', color: '#6B7280' }
    ];

    const existingNames = existingCategories.map(cat => cat.name);
    const missingCategories = requiredCategories.filter(
      required => !existingNames.includes(required.name)
    );

    if (missingCategories.length > 0) {
      try {
        const { error } = await supabase
          .from('categories')
          .insert(
            missingCategories.map(cat => ({
              ...cat,
              user_id: user.id,
              type: 'expense',
              is_default: true // Mark as system default category
            }))
          );
        if (error) {
          console.error('Error agregando categorías faltantes:', error);
        }
      } catch (error) {
        console.error('Error en ensureAllCategories:', error);
      }
    }
  }, [supabase, user]);

  const createDefaultCategories = useCallback(async () => {
    if (!user) return

    const defaultCategories = [
      { name: 'Alimentación', icon: 'ShoppingCart', color: '#10B981' },
      { name: 'Vivienda', icon: 'Home', color: '#3B82F6' },
      { name: 'Transporte', icon: 'Bus', color: '#F59E0B' },
      { name: 'Servicios', icon: 'Zap', color: '#8B5CF6' },
      { name: 'Salud', icon: 'HeartPulse', color: '#EF4444' },
      { name: 'Entretenimiento', icon: 'Film', color: '#EC4899' },
      { name: 'Deudas y Préstamos', icon: 'Landmark', color: '#7E22CE' },
      { name: 'Educación', icon: 'BookOpen', color: '#06B6D4' },
      { name: 'Comunicación', icon: 'Phone', color: '#059669' },
      { name: 'Seguridad Social', icon: 'Users', color: '#D97706' },
      { name: 'Seguros', icon: 'ShieldCheck', color: '#6366F1' },
      { name: 'General', icon: 'Folder', color: '#6B7280' }
    ]

    try {
      const { data, error } = await supabase
        .from('categories')
        .insert(
          defaultCategories.map(cat => ({
            ...cat,
            user_id: user.id,
            type: 'expense',
            is_default: true // Mark as system default category
          }))
        )
        .select()

      if (error) throw error

      setStateValue({
        categories: data || [],
        loadingCategories: false,
        statusMessage: ''
      })
    } catch (error) {
      console.error('Error creando categorías por defecto:', error)
      setStateValue({ loadingCategories: false, statusMessage: '' })
    }
  }, [supabase, user, setStateValue])

  // Cargar categorías desde Supabase
  const loadCategories = useCallback(async () => {
    if (!user) {
      setStateValue({ error: 'Usuario no autenticado' });
      return;
    }

    try {
      setStateValue({ loadingCategories: true, statusMessage: 'Cargando categorías, por favor espera...' });

      const { data: categories, error } = await supabase
        .from('categories')
        .select('*')
        .eq('user_id', user.id)
        .eq('type', 'expense')
        .order('name', { ascending: true });

      if (error) throw error;

      if (!categories || categories.length === 0) {
        await createDefaultCategories();
        return;
      }

      // Verificar si faltan categorías y agregarlas
      await ensureAllCategories(categories);

      // Recargar categorías después de asegurar que estén completas
      const { data: updatedCategories, error: reloadError } = await supabase
        .from('categories')
        .select('*')
        .eq('user_id', user.id)
        .eq('type', 'expense')
        .order('name', { ascending: true });

      if (reloadError) throw reloadError;

      setStateValue({
        categories: updatedCategories || [],
        loadingCategories: false,
        statusMessage: ''
      });
    } catch (error) {
      console.error('Error cargando categorías:', error);
      setStateValue({
        error: 'Error al cargar las categorías',
        loadingCategories: false,
        statusMessage: ''
      });
    }
  }, [supabase, user, setStateValue, ensureAllCategories, createDefaultCategories]);

  // Detectar si es móvil y cargar categorías
  useEffect(() => {
    // Detectar dispositivo móvil
    const checkMobile = () => {
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
        (window.innerWidth <= 768);
      setStateValue({ isMobile });
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    if (user) {
      loadCategories();
    } else {
      setStateValue({ error: 'Usuario no autenticado' });
    }

    return () => window.removeEventListener('resize', checkMobile);
  }, [user, loadCategories, setStateValue]);

  // (definido arriba) createDefaultCategories

  // handleFileSelect is already defined above with useCallback
  // No need for extra closing brackets and semicolon here

  const convertFileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => {
        const base64 = reader.result as string
        resolve(base64.split(',')[1]) // Remover el prefijo data:image/...;base64,
      }
      reader.onerror = reject
      reader.readAsDataURL(file)
    })
  }

  const scanTickets = async () => {

    if (state.selectedFiles.length === 0) {
      setStateValue({ error: 'Selecciona al menos una imagen' })
      return
    }

    if (state.loadingCategories) {
      setStateValue({ statusMessage: 'Cargando categorías, por favor espera...' })
      return
    }

    if (state.categories.length === 0) {
      setStateValue({ error: 'No hay categorías disponibles. Por favor, intenta recargar la página.' })
      return
    }


    setStateValue({ isScanning: true, error: '' })


    try {
      const processedTickets: ScannedTicket[] = []
      const processingErrors: string[] = []

      for (const file of state.selectedFiles) {

        try {
          // Upload image directly to Supabase Storage
          if (!user) {
            throw new Error('Usuario no autenticado');
          }
          const fileName = `receipts/${user.id}/${Date.now()}_${file.name}`;

          const { data: uploadData, error: uploadError } = await supabase.storage
            .from('receipts')
            .upload(fileName, file, {
              cacheControl: '3600',
              upsert: false
            });

          if (uploadError) {
            console.error('❌ Error subiendo imagen a Supabase Storage:', uploadError);
            throw new Error(`Error al subir imagen: ${uploadError.message}`);
          }

          const { data: urlData } = supabase.storage
            .from('receipts')
            .getPublicUrl(fileName);
          const imageUrl = urlData.publicUrl;

          // Si el archivo proviene de un PDF, también subimos el original PDF para guardar referencia
          let originalPdfUrl: string | null = null
          const originalPdf: File | undefined = (file as any).originalPdf
          if (originalPdf) {
            const pdfPath = `receipts/${user.id}/${Date.now()}_${originalPdf.name}`
            const { error: pdfErr } = await supabase.storage.from('receipts').upload(pdfPath, originalPdf, { cacheControl: '3600', upsert: false })
            if (!pdfErr) {
              const { data: pdfUrlData } = supabase.storage.from('receipts').getPublicUrl(pdfPath)
              originalPdfUrl = pdfUrlData.publicUrl
            } else {
              console.error('Error subiendo PDF original:', pdfErr)
            }
          }



          let result;
          try {
            const response = await fetch('/api/scan-ticket', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                imageUrl: imageUrl,
                mimeType: file.type
              })
            });


            if (!response.ok) {
              let errorData;
              try {
                errorData = await response.clone().json();
                console.error(`API error for ${file.name}:`, errorData);
              } catch (parseError) {
                const textError = await response.text();
                console.error(`Error parsing API error response for ${file.name}:`, textError);
                throw new Error(`Error ${response.status}: ${response.statusText} - ${textError.substring(0, 200)}`);
              }
              throw new Error(errorData.error || `Error ${response.status}: ${response.statusText}`);
            }

            result = await response.json();


            if (!result.success) {
              console.error(`API result success false for ${file.name}:`, result.error);
              throw new Error(result.error || 'Error desconocido al procesar la imagen');
            }

            if (result.ticket) {

              processedTickets.push({
                amount: parseFloat(result.ticket.amount) || 0,
                description: result.ticket.description || `Ticket de ${result.ticket.merchant || 'compra'}`,
                date: result.ticket.date || (() => {
                  const today = new Date();
                  const year = today.getFullYear();
                  const month = String(today.getMonth() + 1).padStart(2, '0');
                  const day = String(today.getDate()).padStart(2, '0');
                  return `${year}-${month}-${day}`;
                })(),
                merchant: result.ticket.merchant || 'Establecimiento',
                category: result.ticket.category || 'General',
                imageFile: file,
                originalPdfUrl: originalPdfUrl || null
              });
            }
          } catch (fetchError: any) {
            console.error(`Error processing ${file.name}:`, fetchError);
            console.error('Error completo:', JSON.stringify(fetchError, null, 2));
            const errorMessage = fetchError.message || 'Error desconocido';
            processingErrors.push(
              `${file.name}: ${errorMessage}`
            );
          }
        } catch (err) {
          console.error(`Error procesando ${file.name} en bucle interno:`, err);
          processingErrors.push(
            `No se pudo procesar ${file.name}: ${err instanceof Error ? err.message : 'Error desconocido'}`
          );
        }
      }


      // Actualizar estado con resultados

      setStateValue({
        scannedTickets: [...state.scannedTickets, ...processedTickets],
        selectedFiles: [],
        isScanning: false,
        error: processingErrors.length > 0 ? processingErrors.join('; ') : ''
      });


    } catch (err) {
      console.error('❌ Error en scanTickets (catch externo):', err)
      setStateValue({
        isScanning: false,
        error: 'No se pudieron procesar los tickets. Verifica que las imágenes sean legibles e intenta de nuevo.'
      })
    }
  }

  const saveAllTickets = async () => {
    if (!user) {
      setStateValue({ error: 'No se puede guardar los gastos: usuario no autenticado.' });
      return;
    }

    if (state.scannedTickets.length === 0) {
      setStateValue({ error: 'No hay tickets para guardar.' });
      return;
    }

    setStateValue({ isScanning: true, error: '' });

    try {
      const savedTickets: ScannedTicket[] = [];
      const errors: string[] = [];

      for (const ticket of state.scannedTickets) {
        try {
          const categoryId = getCategoryId(ticket.category || 'General');
          if (!categoryId) {
            errors.push(`${ticket.description}: No se pudo encontrar categoría válida`);
            continue;
          }

          // Guardar imagen del recibo si existe
          let receiptImageUrl = null;
          if (ticket.imageFile) {

            const fileName = `receipts/${user.id}/${Date.now()}_${ticket.imageFile.name}`;

            const { data: uploadData, error: uploadError } = await supabase.storage
              .from('receipts')
              .upload(fileName, ticket.imageFile);

            if (uploadError) {
              console.error('❌ Error subiendo imagen del recibo:', uploadError);
            } else {
              const { data: urlData } = supabase.storage
                .from('receipts')
                .getPublicUrl(fileName);
              receiptImageUrl = urlData.publicUrl;
            }
          }

          const { error } = await supabase.from('transactions').insert({
            user_id: user.id,
            amount: ticket.amount,
            description: ticket.description,
            category_id: categoryId,
            date: ticket.date,
            type: 'expense',
            receipt_image_url: receiptImageUrl
          });

          if (error) {
            errors.push(`${ticket.description}: ${error.message}`);
          } else {
            savedTickets.push(ticket);
          }
        } catch (error: any) {
          errors.push(`${ticket.description}: ${error.message}`);
        }
      }

      if (savedTickets.length > 0) {
        // Limpiar todos los tickets guardados
        setStateValue({
          scannedTickets: [],
          isScanning: false,
          error: errors.length > 0 ? `Algunos tickets tuvieron errores: ${errors.join('; ')}` : ''
        });

        // Notificar al componente padre con TODOS los tickets guardados
        onTicketProcessed?.(savedTickets);
        try {
          // Pequeño delay para asegurar que las inserciones estén visibles, luego emite evento global
          await new Promise(r => setTimeout(r, 120));
          window.dispatchEvent(new CustomEvent('pw:transactions:saved', { detail: { count: savedTickets.length } }))
        } catch { }

        // Cerrar el modal inmediatamente
        onClose?.();
      } else {
        setStateValue({
          isScanning: false,
          error: `No se pudo guardar ningún ticket: ${errors.join('; ')}`
        });
      }


    } catch (error: any) {
      setStateValue({
        isScanning: false,
        error: 'No se pudieron guardar los gastos. Intenta de nuevo.'
      });
    }
  };

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    if (!acceptedFiles || acceptedFiles.length === 0) return;

    // Mostrar mensaje breve pero NO activar isScanning; el escaneo solo inicia al pulsar el botón
    setStateValue({
      statusMessage: 'Imágenes cargadas. Listas para escanear.'
    });

    // Pequeño retraso para permitir que se actualice la UI
    await new Promise(resolve => setTimeout(resolve, 50));

    // Agregar archivos seleccionados
    handleFileSelect({ target: { files: acceptedFiles } });

    // Limpiar el mensaje luego de añadirlos
    setStateValue({ statusMessage: '' });
  }, [handleFileSelect, setStateValue]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    noClick: true, // Prevent opening file dialog on click of the dropzone itself
    accept: {
      'image/jpeg': ['.jpeg', '.jpg'],
      'image/png': ['.png'],
      'image/webp': ['.webp'],
    },
    maxSize: 10 * 1024 * 1024, // 10MB
    multiple: true,
    onDropRejected: (fileRejections) => {
      const errors = fileRejections.map(rejection => {
        if (rejection.errors.some(e => e.code === 'file-too-large')) {
          return `El archivo ${rejection.file.name} es demasiado grande (máx. 10MB)`;
        }
        if (rejection.errors.some(e => e.code === 'file-invalid-type')) {
          return `Formato no soportado: ${rejection.file.name}. Usa JPG, PNG o WebP.`;
        }
        return `Error al cargar ${rejection.file.name}`;
      });

      setStateValue({
        error: errors.join('\n'),
        isScanning: false,
        statusMessage: ''
      });
    }
  });

  return (
    <div className="w-full">
      <Card className="w-full border-0 shadow-none bg-transparent">
        <CardHeader>
          <div className="flex justify-between items-center">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Camera className="w-6 h-6" /> Escáner de Tickets
              </CardTitle>
              <div className="text-sm text-muted-foreground">
                Sube una imagen de tu ticket para extraer la información.
              </div>
            </div>
          </div>
        </CardHeader>

        <CardContent className="space-y-4 p-4 min-h-0">
          {state.error && (
            <div className="p-3 bg-red-50 text-red-700 rounded-md text-sm">
              {state.error}
            </div>
          )}

          {state.scannedTickets.length === 0 ? (
            <div className="space-y-3">
              <div
                {...getRootProps()}
                className={`border-2 border-dashed rounded-lg p-4 text-center transition-colors ${isDragActive ? 'border-primary bg-primary/10' : 'border-gray-300 bg-gray-50'}`}
              >
                {/* Dropzone input for drag & drop only */}
                <input {...getInputProps()} ref={fileInputRef} className="hidden" multiple />
                <MousePointerSquareDashed className="mx-auto h-8 w-8 text-gray-400 mb-2" />
                <p className="text-sm text-gray-600">
                  Arrastra y suelta tus imágenes aquí, o haz clic para seleccionar.
                </p>
                <p className="text-xs text-gray-500">(JPG, PNG, WebP - Máx. 10MB por imagen, hasta 5 imágenes)</p>
                {state.selectedFiles.length > 0 && (
                  <p className="mt-2 text-xs text-gray-700">
                    {state.selectedFiles.length} imagen(es) seleccionada(s).
                  </p>
                )}
              </div>

              <div className="flex flex-col gap-2 sm:flex-row">
                {/* Input para seleccionar solo imágenes */}
                <input
                  ref={pickerInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileSelect}
                  className="hidden"
                  multiple
                />

                <Button
                  onClick={() => pickerInputRef.current?.click()}
                  className="flex-1 flex items-center gap-2 text-sm h-10"
                  disabled={state.isScanning}
                >
                  <Upload className="w-4 h-4" /> Seleccionar Imágenes
                </Button>

                {state.isMobile && (
                  <Button
                    onClick={() => cameraInputRef.current?.click()}
                    className="flex-1 flex items-center gap-2 text-sm h-10"
                    disabled={state.isScanning}
                    variant="outline"
                  >
                    <Video className="w-4 h-4" /> Usar Cámara
                  </Button>
                )}
              </div>

              {/* Input oculto para cámara */}
              <input
                ref={cameraInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handleFileSelect}
                className="hidden"
                multiple
              />

              <Button
                onClick={scanTickets}
                className="w-full flex items-center gap-2 text-sm h-10"
                disabled={state.selectedFiles.length === 0 || state.isScanning}
              >
                {state.isScanning ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" /> Escaneando...
                  </>
                ) : (
                  <>
                    <CameraIcon className="w-4 h-4" /> Escanear Ticket(s)
                  </>
                )}
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold">Tickets Escaneados:</h3>
                <Button
                  onClick={() => setStateValue({ scannedTickets: [] })}
                  variant="outline"
                  size="sm"
                  className="flex items-center gap-2"
                >
                  <Camera className="w-4 h-4" />
                  Escanear más
                </Button>
              </div>

              <div className="space-y-3 max-h-[50vh] overflow-y-auto">
                {state.scannedTickets.map((ticket, index) => (
                  <Card key={index} className="p-4 border border-border">
                    <div className="flex justify-between items-center mb-2">
                      <h4 className="font-bold text-sm">{ticket.description || 'Ticket sin descripción'}</h4>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setState(prev => ({ ...prev, scannedTickets: prev.scannedTickets.filter((_, i) => i !== index) }))}
                        className="h-6 w-6 p-0"
                        title="Eliminar ticket"
                      >
                        <X className="w-3 h-3" />
                      </Button>
                    </div>
                    <div className="space-y-1 text-sm">
                      <p>Monto: {formatCurrency(ticket.amount)}</p>
                      <p>Fecha: {ticket.date}</p>
                      {ticket.merchant && <p>Comercio: {ticket.merchant}</p>}
                      {ticket.category && (
                        <p className="flex items-center gap-1">
                          Categoría: {ticket.category}
                        </p>
                      )}
                    </div>
                  </Card>
                ))}
              </div>

              <Button
                onClick={saveAllTickets}
                className="w-full flex items-center gap-2 h-10 mt-4"
                disabled={state.isScanning || state.scannedTickets.length === 0}
              >
                {state.isScanning ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" /> Guardando...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" /> Guardar Todos los Gastos ({state.scannedTickets.length})
                  </>
                )}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

export default TicketScanner;